/**
 * Procrastination Blackjack (SPEC.md §9).
 *
 * The whole game runs here, in the browser, and nothing about a hand is persisted or sent
 * anywhere — it is a way of choosing what to do next, not a thing worth recording. The
 * follow-through is tracked already: you tick the tasks off, and completion is recorded
 * the same way it is everywhere else in the app.
 *
 * §9.2's three slots each independently accept a typed title, a fuzzy search result, or an
 * auto-pick from `/api/slipping` (highest staleness first, skipping anything already in
 * another slot). A typed title is the only thing on this page that writes: it creates a
 * real task through the ordinary task endpoint.
 *
 * §9.3 is the one place to be generous with motion: cards deal in with a flip, the total
 * ticks up as they land, and the outcome fires a celebration scaled to what happened —
 * a 21 feels genuinely good, a bust lands as comic rather than punishing. All of it is
 * CSS transform/opacity, and `prefers-reduced-motion` is honoured in styles.css.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ApiError, api, wasFiledToInbox, type SlippingEntry, type Task } from "../api";
import { BadgeGlossary, type GlossaryEntry } from "../components/BadgeGlossary";
import { Die } from "../components/Die";
import { Calendar, Important, Star } from "../components/icons";
import { energyTitle, isOverdue } from "../components/taskFormat";
import { celebrate, celebrateProject, useFireworks } from "../components/fireworksContext";
import { MAX_ENERGY, pickForSlot } from "../autoPick";
import { parseDestination, recalledDestination } from "../destination";
import {
  MAX_CARDS,
  OUTCOME_TEXT,
  SLOT_COUNT,
  TASKS_OWED,
  type Card,
  type Hand,
  canHit,
  deal,
  handTotal,
  hit,
  type Outcome,
  outcomeBands,
  outcomeFor,
  stand,
} from "../blackjack";

const RED_SUITS = new Set(["H", "D"]);
const SUIT_GLYPHS: Record<string, string> = { S: "♠", H: "♥", D: "♦", C: "♣" };

const RULES_GLOSSARY: GlossaryEntry[] = [
  {
    key: "hand",
    title: "The hand",
    preview: null,
    description:
      "A real hand of blackjack: aces count 11 or 1, dealer stands on 17, six cards is an " +
      "automatic stop either way.",
  },
  {
    key: "slots",
    title: "The three slots",
    preview: null,
    description:
      "Each slot gets filled your way: type in a new task, search for one you already have, " +
      "or roll the dice for a low-energy pick from whatever's gone quiet.",
  },
  {
    key: "stakes",
    title: "What's owed",
    preview: null,
    description:
      "21 clean and nothing's due. 18-20 and one of the three is due. 17 or under, two are " +
      "due. Bust the hand entirely and all three are due.",
  },
  {
    key: "point",
    title: "The point",
    preview: null,
    description:
      "Committing to something concrete, however the cards land, beats staring at a full " +
      "list deciding nothing's small enough to start with. Losing the hand is the good " +
      "outcome here.",
  },
];

type SlotState = {
  title: string;
  taskId: string | null;
  taskTitle: string | null;
  /**
   * What the chosen task costs. Held on the slot rather than looked up later: a task picked
   * by search is not on the slipping list, so looking it up there returned null and the
   * felt showed it as unrated.
   */
  energy: number | null;
  /**
   * The signals worth seeing *while choosing*: important, starred, and a date that has
   * passed. Counts — chosen ×N, surfaced, deferred — are deliberately not carried. They
   * are history, and history is what you weigh things up with; the point of a hand is to
   * stop weighing things up.
   */
  flags: TaskFlags;
};

/** Important, starred and due — the three the felt shows. */
type TaskFlags = { important: boolean; starred: boolean; due: string | null };

const NO_FLAGS: TaskFlags = { important: false, starred: false, due: null };

const EMPTY_SLOT: SlotState = {
  title: "",
  taskId: null,
  taskTitle: null,
  energy: null,
  flags: NO_FLAGS,
};

/** A task in play. Carries just enough to tick it off and say why it matters. */
type Chosen = {
  id: string | null;
  title: string;
  energy: number | null;
  done: boolean;
  flags: TaskFlags;
};

export function BlackjackPage() {
  const [slots, setSlots] = useState<SlotState[]>(() =>
    Array.from({ length: SLOT_COUNT }, () => ({ ...EMPTY_SLOT })),
  );
  const [chosen, setChosen] = useState<Chosen[] | null>(null);
  const [hand, setHand] = useState<Hand | null>(null);
  const [stale, setStale] = useState<SlippingEntry[]>([]);
  /**
   * What the dice draw from when nothing is slipping.
   *
   * §9.2 draws from `/api/slipping`, which is right — the game exists to surface what you
   * have been avoiding. But a database where nothing has passed its threshold made all
   * fifteen dice inert with no explanation, and an unplayable game teaches you not to open
   * it. So the dice fall back to open tasks and say which pool they drew from.
   */
  const [openTasks, setOpenTasks] = useState<SlippingEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const burst = useFireworks();

  const loadStale = useCallback(() => {
    // §8.6.4: a Next Action is the actionable unit of a breakdown, and the parent it
    // belongs to may not itself be something you can sit down and do — eligible here
    // alongside parents and standalone tasks. Every other subtask stays excluded.
    api
      .slipping(true)
      .then((body) => setStale(body.tasks))
      .catch(() => setStale([]));
  }, []);

  useEffect(loadStale, [loadStale]);

  useEffect(() => {
    api
      .tasks({ limit: 100, include_next_action_subtasks: "true" })
      .then((body) =>
        setOpenTasks(
          body.tasks.map((task) => ({
            id: task.id,
            name: task.title,
            // Not slipping, so it has no staleness to weigh. Equal weights across the
            // pool is the honest reading of "nothing here is overdue attention".
            days_since_touch: 0,
            stale_after_days: 1,
            over_by: 0,
            energy: task.energy,
            due: task.due,
            important: task.important,
            starred: task.starred,
          })),
        ),
      )
      .catch(() => setOpenTasks([]));
  }, []);

  // Slipping if there is any, otherwise everything open. Never both: mixing them would let
  // a fresh task outrank one you have been dodging for a fortnight.
  const pool = stale.length > 0 ? stale : openTasks;
  const drawingFromSlipping = stale.length > 0;

  const update = useCallback((index: number, next: Partial<SlotState>) => {
    setSlots((current) =>
      current.map((slot, i) => (i === index ? { ...slot, ...next } : slot)),
    );
  }, []);

  /**
   * Roll one slot at a given activation cost.
   *
   * Per slot rather than for the whole table, because "what I have in me" is not one number
   * across three tasks. A realistic morning is one thing at a 4 and two small wins at a 1 —
   * a single ceiling for all three cannot express that, and the old global picker made you
   * choose between an honest ceiling and a useful hand.
   */
  const rollSlot = useCallback(
    (index: number, ceiling: number) => {
      const taken = new Set(slots.map((s) => s.taskId).filter(Boolean) as string[]);
      const { entry, reached } = pickForSlot(pool, { ceiling, taken });

      if (!entry) {
        setError("Nothing left to draw at all - type this one yourself.");
        return;
      }

      setSlots((current) =>
        current.map((slot, i) =>
          i === index
            ? {
                taskId: entry.id,
                taskTitle: entry.name,
                title: "",
                energy: entry.energy ?? null,
                flags: {
                  important: Boolean(entry.important),
                  starred: Boolean(entry.starred),
                  due: entry.due ?? null,
                },
              }
            : slot,
        ),
      );
      setError(
        reached > ceiling
          ? `Nothing left at ${ceiling} or under, so slot ${index + 1} went up to ${reached}.`
          : null,
      );
    },
    [slots, pool],
  );

  const ready = slots.every((slot) => slot.taskId || slot.title.trim());

  async function startHand() {
    setBusy(true);
    setError(null);
    try {
      // §9.2 option 1: a freshly typed title creates a task. That is the only write here.
      //
      // It is filed to whichever area or project the briefing's picker last used — the
      // user's own standing choice. Without one the server holds it in the inbox rather
      // than guessing (§0), and the hand says so, because a slot you cannot tick off
      // afterwards is a slot that quietly does nothing.
      const destination = parseDestination(recalledDestination());
      const picked = await Promise.all(
        slots.map(async (slot): Promise<Chosen> => {
          if (slot.taskId) {
            return {
              id: slot.taskId,
              title: slot.taskTitle ?? "(untitled)",
              energy: slot.energy,
              done: false,
              flags: slot.flags,
            };
          }
          const created = await api.createTask({ title: slot.title.trim(), ...destination });
          return wasFiledToInbox(created)
            ? {
                id: null,
                title: `${created.item.raw_text}  (in the inbox - no area set)`,
                energy: null,
                done: false,
                flags: NO_FLAGS,
              }
            : {
                id: created.id,
                title: created.title,
                energy: created.energy,
                done: false,
                flags: {
                  important: created.important,
                  starred: created.starred,
                  due: created.due,
                },
              };
        }),
      );
      setChosen(picked);
      setHand(deal());
    } catch (err) {
      if (!(err instanceof ApiError && err.isAuth)) {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  function again() {
    setHand(null);
    setChosen(null);
    setSlots(Array.from({ length: SLOT_COUNT }, () => ({ ...EMPTY_SLOT })));
    setError(null);
    loadStale();
  }

  if (hand && chosen) {
    return (
      <HandView
        hand={hand}
        tasks={chosen}
        onHit={() => setHand(hit(hand))}
        onStand={() => setHand(stand(hand))}
        onAgain={again}
        onComplete={async (taskId, at) => {
          const task = chosen.find((c) => c.id === taskId);
          if (task) celebrate(burst, at.x, at.y, task.energy);
          await api.completeTask(taskId);
          setChosen((current) =>
            (current ?? []).map((c) => (c.id === taskId ? { ...c, done: true } : c)),
          );
        }}
      />
    );
  }

  return (
    <section className="panel">
      <header>
        <h2>Procrastination Blackjack</h2>
        <span className="row">
          <span className="muted small">Three things you've been avoiding</span>
          <BadgeGlossary entries={RULES_GLOSSARY} title="How it works" />
        </span>
      </header>

      {/*
        A table, not a sentence. Four bands with a number on one side and a consequence on
        the other is tabular data, and reading it as prose meant re-parsing it every hand.
        Still generated from `outcomeFor`, so it cannot drift from what is actually played.
      */}
      <table className="rules">
        <tbody>
          {outcomeBands().map((band) => (
            <tr key={band.range} className={band.outcome}>
              <th scope="row">{band.range}</th>
              <td>{band.owed}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="slots">
        {slots.map((slot, index) => (
          <SlotInput
            key={index}
            index={index}
            slot={slot}
            onChange={(next) => update(index, next)}
            onRoll={(ceiling) => rollSlot(index, ceiling)}
            canRoll={pool.length > 0}
            fromSlipping={drawingFromSlipping}
          />
        ))}
      </div>

      {error && <p className="error">{error}</p>}

      <button
        className="btn primary"
        onClick={() => void startHand()}
        disabled={!ready || busy}
      >
        {busy ? "Shuffling…" : "Deal"}
      </button>
    </section>
  );
}

function SlotInput({
  index,
  slot,
  onChange,
  onRoll,
  canRoll,
  fromSlipping,
}: {
  index: number;
  slot: SlotState;
  onChange: (next: Partial<SlotState>) => void;
  /** Draw a slipping task costing this much or less into this slot. */
  onRoll: (ceiling: number) => void;
  canRoll: boolean;
  /** False when the pool fell back to open tasks, so the tooltip can say so. */
  fromSlipping: boolean;
}) {
  const [matches, setMatches] = useState<Task[]>([]);
  const debounce = useRef<number | undefined>(undefined);

  // §9.2: fuzzy search against existing task titles, as a dropdown.
  useEffect(() => {
    window.clearTimeout(debounce.current);
    const query = slot.title.trim();
    if (slot.taskId || query.length < 2) {
      setMatches([]);
      return;
    }
    debounce.current = window.setTimeout(() => {
      api
        .tasks({ q: query, limit: 6, include_next_action_subtasks: "true" })
        .then((body) => setMatches(body.tasks))
        .catch(() => setMatches([]));
    }, 180);
    return () => window.clearTimeout(debounce.current);
  }, [slot.title, slot.taskId]);

  if (slot.taskId) {
    return (
      <div className="slot">
        <div className="slot-head">
          <span>Slot {index + 1}</span>
          <button className="btn ghost small" onClick={() => onChange({ ...EMPTY_SLOT })}>
            Clear
          </button>
        </div>
        {/*
          `slot-filled` matches the height of the input it replaces, so a slot does not
          shrink as it is filled and the three stop shuffling about under the cursor.
        */}
        <div className="slot-filled">
          <strong>{slot.taskTitle}</strong>
          <span className="badge energy" title={energyTitle(slot.energy)}>
            <Die value={slot.energy} title={energyTitle(slot.energy)} />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="slot">
      {/*
        The dice replace the auto-pick button rather than sitting alongside it. Two controls
        for one job - a global ceiling and a per-slot button - meant neither said what it
        did, and the ceiling could not express the hand you actually want.
      */}
      <div className="slot-head">
        <span>Slot {index + 1}</span>
        <span className="energy-picker" role="group" aria-label={`Roll slot ${index + 1}`}>
          {Array.from({ length: MAX_ENERGY }, (_, i) => i + 1).map((level) => (
            <button
              key={level}
              type="button"
              className="btn ghost energy-pip"
              onClick={() => onRoll(level)}
              disabled={!canRoll}
              aria-label={`Roll slot ${index + 1} at activation cost ${level} or less`}
              title={
                canRoll
                  ? fromSlipping
                    ? `Draw something slipping that costs ${level} or less`
                    : `Nothing is slipping - draw any open task costing ${level} or less`
                  : "Nothing to draw: there are no open tasks yet"
              }
            >
              <Die value={level} size={18} />
            </button>
          ))}
        </span>
      </div>

      <input
        className="input"
        placeholder="Type a new task, or search an existing one"
        aria-label={`Task for slot ${index + 1}`}
        value={slot.title}
        onChange={(e) => onChange({ title: e.target.value })}
      />

      {matches.length > 0 && (
        <ul className="suggestions">
          {matches.map((task) => (
            <li key={task.id}>
              <button
                onClick={() =>
                  onChange({
                    taskId: task.id,
                    taskTitle: task.title,
                    title: "",
                    energy: task.energy,
                    flags: {
                      important: task.important,
                      starred: task.starred,
                      due: task.due,
                    },
                  })
                }
              >
                {task.title}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function HandView({
  hand,
  tasks,
  onHit,
  onStand,
  onAgain,
  onComplete,
}: {
  hand: Hand;
  tasks: Chosen[];
  onHit: () => void;
  onStand: () => void;
  onAgain: () => void;
  onComplete: (taskId: string, at: { x: number; y: number }) => Promise<void>;
}) {
  const total = handTotal(hand.cards);
  const shown = useTickingTotal(total);
  const outcome = hand.finished ? outcomeFor(total) : null;
  const row = useCenteringRow(hand.cards.length);
  const banner = useRef<HTMLDivElement>(null);
  useOutcomeCelebration(outcome, banner);

  return (
    <section className="panel">
      <header>
        <h2>The hand</h2>
        <span className="muted small">
          {hand.cards.length} of {MAX_CARDS} cards
        </span>
      </header>

      <div className="felt">
        <div className="hand-row" ref={row}>
          {hand.cards.map((card, index) => (
            <PlayingCard key={`${card.rank}${card.suit}-${index}`} card={card} index={index} />
          ))}
        </div>
      </div>

      <div className="row spread" style={{ marginBottom: "0.75rem" }}>
        <span className="total" aria-live="polite">
          {shown}
        </span>
        {!hand.finished && (
          <span className="row">
            <button className="btn" onClick={onHit} disabled={!canHit(hand)}>
              Hit
            </button>
            <button className="btn primary" onClick={onStand}>
              Stand
            </button>
          </span>
        )}
      </div>

      {outcome && (
        <div className={`outcome ${outcome}`} ref={banner} role="status">
          <strong>{OUTCOME_TEXT[outcome]}</strong>
          {outcome !== "none" && (
            <p className="small" style={{ margin: "0.35rem 0 0" }}>
              Pick {TASKS_OWED[outcome] === 3 ? "all three" : TASKS_OWED[outcome]} and start
              now.
            </p>
          )}
        </div>
      )}

      {/*
        Spare, but not silent. §9 says "keep it light and don't build a casino", and the
        first version took that to mean cost and a tick and nothing else - which left you
        choosing between three titles with no idea that one was overdue and one you had
        already picked for today.
        So: important, starred, and a date that has passed. The counts stay off. Those are
        history, and weighing up history is exactly what a hand exists to stop.
      */}
      <ul className="tasks felt-tasks">
        {tasks.map((task, index) => (
          <li className={`task${task.done ? " completing" : ""}`} key={`${task.id}-${index}`}>
            <button
              className="tick"
              disabled={!task.id || task.done}
              onClick={(e) => {
                const box = e.currentTarget.closest("li")!.getBoundingClientRect();
                if (task.id) {
                  void onComplete(task.id, {
                    x: box.left + Math.min(box.width * 0.4, 220),
                    y: box.top + box.height / 2,
                  });
                }
              }}
              aria-label={task.done ? `${task.title} is done` : `Complete: ${task.title}`}
              title={task.id ? "Done it" : "In the inbox - file it before you can tick it"}
            >
              {task.done ? "✓" : ""}
            </button>
            <span className="title">
              <span className="text">{task.title}</span>
              <span className="badges">
                {task.energy !== null && (
                  <span className="badge energy" title={energyTitle(task.energy)}>
                    <Die value={task.energy} title={energyTitle(task.energy)} />
                  </span>
                )}
                {task.flags.starred && (
                  <span className="badge star">
                    <Star filled /> starred
                  </span>
                )}
                {task.flags.important && (
                  <span className="badge important">
                    <Important /> important
                  </span>
                )}
                {task.flags.due &&
                  isOverdue(task.flags.due, new Date().toLocaleDateString("en-CA")) && (
                    <span className="badge overdue" title="Overdue">
                      <Calendar /> {task.flags.due}
                    </span>
                  )}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {hand.finished && (
        <button className="btn" onClick={onAgain} style={{ marginTop: "0.75rem" }}>
          Another hand
        </button>
      )}
    </section>
  );
}

function PlayingCard({ card, index }: { card: Card; index: number }) {
  return (
    <div
      className={`card${RED_SUITS.has(card.suit) ? " red" : ""}`}
      // The stagger, so a two-card deal reads as two cards rather than one flash (§9.3).
      style={{ animationDelay: `${Math.min(index, 5) * 90}ms` }}
      aria-label={`${card.rank} of ${card.suit}`}
    >
      <span>
        {card.rank}
        {SUIT_GLYPHS[card.suit] ?? card.suit}
      </span>
    </div>
  );
}

/**
 * §9.3: the hand's own celebration, fired once when it finishes.
 *
 * Scaled to the draw rather than to the work: a 21 gets the five-shot volley a finished
 * project gets, 18–20 a single burst at the top of the scale, and standing at 17 or less a
 * modest one — you chose to stop, which is a decision worth marking even though it costs
 * you two tasks.
 *
 * A bust gets nothing. It is meant to land as comic rather than punishing, and fireworks
 * over "do all three" would read as the app enjoying itself at your expense.
 */
function useOutcomeCelebration(
  outcome: Outcome | null,
  /** The result banner, so the burst lands on the thing you are reading. */
  target: React.RefObject<HTMLElement | null>,
) {
  const burst = useFireworks();
  const fired = useRef<Outcome | null>(null);

  useEffect(() => {
    if (!outcome || fired.current === outcome) return;
    fired.current = outcome;

    // The 21 is deliberately screen-wide. It is the one that should not be contained.
    if (outcome === "none") {
      celebrateProject(burst);
      return;
    }
    const level = outcome === "one" ? 5 : outcome === "two" ? 3 : null;
    if (level === null) return;

    // Measured rather than guessed at a fraction of the viewport, which put it below
    // everything worth looking at on a tall window.
    const box = target.current?.getBoundingClientRect();
    burst(
      box ? box.left + box.width / 2 : window.innerWidth / 2,
      box ? box.top + box.height / 2 : window.innerHeight / 2,
      level,
    );
  }, [outcome, burst, target]);
}

/**
 * Keep the hand centred, and ease the shift when a card lands.
 *
 * A centred row jumps sideways by half a card the instant you hit, which is the one moment
 * you are looking straight at it. Flexbox cannot transition a layout change, so this is the
 * usual first-last-invert: start the row where it just was, then let it slide to where it
 * now belongs.
 *
 * Transform only, so it costs nothing to composite (§9.3). If the row has wrapped to a
 * second line the horizontal delta is meaningless, so it is skipped rather than guessed at.
 */
function useCenteringRow(cardCount: number) {
  const row = useRef<HTMLDivElement>(null);
  const previous = useRef<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const element = row.current;
    if (!element) return;

    const { width, height } = element.getBoundingClientRect();
    const before = previous.current;
    previous.current = { width, height };

    if (!before || before.width === width || before.height !== height) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    element.animate(
      [{ transform: `translateX(${(width - before.width) / 2}px)` }, { transform: "none" }],
      { duration: 320, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
    );
  }, [cardCount]);

  return row;
}

/** §9.3: "totals tick up as they land". */
function useTickingTotal(target: number): number {
  const [shown, setShown] = useState(target);

  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(target);
      return;
    }

    let current = shown;
    if (current === target) return;

    const step = current < target ? 1 : -1;
    const timer = window.setInterval(() => {
      current += step;
      setShown(current);
      if (current === target) window.clearInterval(timer);
    }, 45);
    return () => window.clearInterval(timer);
    // `shown` is deliberately not a dependency: including it would restart the animation
    // on every tick and the count would never arrive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return shown;
}
